<template>
  <div
    class="relative bg-store-bg bg-fixed bg-cover bg-top bg-[#00284F] py-[80px] px-2 sm:px-10 h-[5000px]"
  >
    <!-- Content -->
    <div
      class="relative z-10 flex flex-col items-center text-white text-center py-12 px-4 sm:px-6"
    >
      <!-- Title -->
      <h1
        v-motion-pop-visible
        class="text-5xl md:text-[70px] font-black uppercase font-golos"
      >
        STORE
      </h1>

      <p v-motion-pop-visible class="font-montserrat mt-4 text-2xl">CLAIM YOUR REWARDS</p>

      <div
        v-motion-pop-visible
        class="relative w-full max-w-sm sm:max-w-xl mt-10 p-4 lg:p-6 flex justify-between items-center rounded-3xl bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] backdrop-blur-lg"
      >
        <div class="flex items-center">
          <img
            src="../assets/credit-card.avif"
            alt="Points Icon"
            class="w-8 sm:w-16 h-8 sm:h-16 mr-3"
          />
          <div>
            <p class="text-white text-xs sm:text-sm lg:text-lg font-base">
              Available Points
            </p>
            <span
              v-if="loading || animatedPoints === null"
              class="inline-block w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin mt-2"
            ></span>
            <p
              v-else
              class="text-white text-xs sm:text-sm lg:text-xl font-bold font-orbitron"
            >
              {{ animatedPoints.toLocaleString() }}
              {{ animatedPoints === 1 ? "point" : "points" }}
            </p>
          </div>
        </div>
        <div class="flex items-center">
          <img
            src="../assets/shopping.avif"
            alt="Items Icon"
            class="w-8 sm:w-16 h-8 sm:h-16 mr-3"
          />
          <div>
            <p class="text-white text-xs sm:text-sm lg:text-lg font-base">
              Available Items
            </p>
            <span
              v-if="loading || animatedTotalItems === null"
              class="inline-block w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin mt-2"
            ></span>
            <p
              v-else
              class="text-white text-xs sm:text-sm lg:text-xl font-bold font-orbitron"
            >
              {{ animatedTotalItems.toLocaleString() }}
              {{ animatedTotalItems === 1 ? "item" : "items" }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Centered search + filter with spacing between -->
    <div class="relative flex justify-center z-30 w-full mt-10 px-4 lg:px-0">
      <div
        v-motion-pop-visible
        class="w-full max-w-6xl rounded-lg p-0 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 select-none"
      >
        <!-- Desktop Search Bar & Filter Options -->
        <div class="hidden lg:flex items-center gap-2 mt-6 sm:mt-0">
          <!-- Search Bar -->
          <div
            class="relative flex items-center bg-transparent border border-white rounded-xl px-4 py-4 w-5 sm:w-64"
          >
            <svg
              class="w-5 h-5 text-white mr-2"
              fill="none"
              stroke="white"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
              />
            </svg>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search..."
              class="bg-transparent lg:text-sm xl:text-base text-white placeholder-white focus:outline-none w-full"
            />
          </div>

          <!-- Sort Dropdown -->
          <div class="relative">
            <div
              @click="toggleSortDropdown"
              class="flex items-center justify-between bg-transparent lg:text-sm xl:text-base lg:w-[240px] xl:w-[260px] border border-white rounded-xl px-4 py-4 cursor-pointer select-none relative"
            >
              <span class="text-white">{{ selectedSortOption }}</span>
              <svg
                class="w-4 h-4 text-white ml-6 transition-transform"
                :class="{ 'rotate-180': showSortDropdown }"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <!-- Sort Options Dropdown -->
            <transition name="fade">
              <div
                v-if="showSortDropdown"
                class="absolute top-14 left-0 bg-[#1D1D1D] border border-white rounded-lg shadow-lg lg:w-[240px] xl:w-[260px]"
              >
                <div
                  v-for="option in sortOptions"
                  :key="option.value"
                  @click="applySort(option)"
                  class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all rounded-b-xl"
                  :class="{
                    'text-white bg-[#302f2f] rounded-t-xl':
                      option.label === selectedSortOption,
                    'text-[#B0B0B0]': option.label !== selectedSortOption,
                  }"
                >
                  {{ option.label }}
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Purchase History Button -->
        <button
          @click="fetchTransactions"
          class="hidden lg:flex bg-[#1294F0] text-sm text-white lg:text-sm xl:text-base lg:w-[140px] xl:w-[200px] px-6 py-4 rounded-lg font-semibold hover:opacity-90 mt-6 sm:mt-0"
        >
          Purchase History
        </button>
      </div>
    </div>

    <!-- Disclaimer Search Bar Mobile-->

    <div
      class="relative flex lg:hidden items-center bg-transparent w-full border border-white rounded-xl px-4 py-4"
    >
      <!-- Search Icon (SVG) -->
      <svg
        class="w-5 h-5 text-white mr-2"
        fill="none"
        stroke="white"
        stroke-width="2"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
        />
      </svg>
      <!-- Input Field -->
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search..."
        class="bg-transparent text-white placeholder-white focus:outline-none w-full"
      />
    </div>

    <div class="relative z-30 flex lg:hidden justify-between items-center w-full mt-6">
      <!-- Sort Dropdown -->
      <div class="relative w-full">
        <div
          @click="toggleSortDropdown"
          class="flex items-center justify-between bg-transparent lg:text-sm xl:text-base w-full lg:w-[200px] xl:w-[260px] border border-white rounded-xl px-4 py-4 cursor-pointer select-none"
        >
          <span class="text-white">{{ selectedSortOption }}</span>
          <!-- Dropdown Arrow -->
          <svg
            class="w-4 h-4 text-white ml-6 transition-transform"
            :class="{ 'rotate-180': showSortDropdown }"
            fill="none"
            stroke="white"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <!-- Sort Options Dropdown -->
        <transition name="fade">
          <div
            v-if="showSortDropdown"
            class="absolute top-14 left-0 bg-[#1D1D1D] border border-white rounded-xl shadow-lg w-full"
          >
            <div
              v-for="option in sortOptions"
              :key="option.value"
              @click="applySort(option)"
              class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all rounded-b-xl"
              :class="{
                'text-white bg-[#302f2f] rounded-t-xl':
                  option.label === selectedSortOption,
                'text-[#B0B0B0]': option.label !== selectedSortOption,
              }"
            >
              {{ option.label }}
            </div>
          </div>
        </transition>
      </div>
      <div class="hidden sm:flex justify-end items-center w-full">
        <!-- Purchase History Button -->
        <button
          @click="fetchTransactions"
          class="bg-[#1294F0] text-sm text-white px-6 py-4 rounded-lg font-semibold hover:opacity-90"
        >
          Purchase History
        </button>
      </div>
    </div>

    <div class="flex sm:hidden justify-end items-center w-full mt-10">
      <!-- Purchase History Button -->
      <button
        @click="fetchTransactions"
        class="bg-[#1294F0] text-sm text-white px-6 py-4 rounded-lg font-semibold hover:opacity-90"
      >
        Purchase History
      </button>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="flex justify-center items-center min-h-[300px]">
      <span
        class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
      ></span>
    </div>

    <div v-else class="flex flex-col items-center justify-center w-full px-4">
      <!-- Store Cards -->
      <div
        class="relative z-0 grid w-full max-w-6xl grid-cols-1 gap-6 mt-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <div
          v-for="product in paginatedProducts"
          :key="product._id"
          class="bg-[#262626] py-6 px-4 rounded-lg flex flex-col text-center shadow-lg h-full"
        >
          <!-- Gift Image -->
          <div class="w-24 h-24 sm:w-28 sm:h-28 mx-auto">
            <img
              :src="
                product.imageUrl ? product.imageUrl : require('../assets/gift-blue.png')
              "
              alt="Gift"
              class="w-full h-full object-contain aspect-square"
            />
          </div>

          <!-- Title & Description Container -->
          <div class="mt-4 text-white flex flex-col flex-grow items-center text-center">
            <!-- Product Title -->
            <h3
              class="font-bold text-sm capitalize line-clamp-2 leading-snug w-full h-[3rem]"
            >
              {{ product.title }}
            </h3>

            <!-- Product Description -->
            <p class="text-[#7F85A4] text-sm line-clamp-3 text-center mt-1">
              {{ product.description }}
            </p>
          </div>

          <!-- Buy Button -->
          <button
            @click="showConfirm(product)"
            :disabled="!isUserLoggedIn"
            class="mt-4 px-6 py-2 text-lg sm:text-base bg-[#1294F0] text-white font-bold rounded-full hover:bg-[#1293f0b6] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ (product.requiredPoints || 0).toLocaleString() }}
            {{ product.requiredPoints === 1 ? "point" : "points" }}
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div class="w-full max-w-6xl mt-10 border-2 border-[#363636] rounded-lg">
        <div
          v-motion-pop-visible
          class="bg-gradient-to-r from-[#1D1D1D] to-[#313B44] rounded-lg p-4 flex items-center justify-between select-none"
        >
          <!-- Left Section: Page Info -->
          <div class="flex items-center gap-2">
            <img
              src="@/assets/search.webp"
              alt="Search"
              class="w-10 h-10 sm:w-14 sm:h-14 object-contain"
            />
            <span class="text-white font-bold text-base sm:text-lg font-montserrat">
              {{ itemStart }} - {{ itemEnd }} of {{ totalItems }}
              Items
            </span>
          </div>

          <!-- Pagination Controls -->
          <div class="flex items-center gap-2">
            <!-- Previous Button -->
            <button
              class="p-2 bg-white rounded-md disabled:opacity-50"
              :disabled="currentPage === 1"
              @click="prevPage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#161E22"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <!-- Next Button -->
            <button
              class="p-2 bg-white rounded-md disabled:opacity-50"
              :disabled="currentPage === totalPages"
              @click="nextPage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="#161E22"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <StoreSuccessModal
    :showSuccessModal="showSuccessModal"
    :ticket="ticket"
    @closeSuccessModal="showSuccessModal = false"
  />

  <StoreErrorModal
    :showErrorModal="showErrorModal"
    :errorTitle="errorTitle"
    :errorMessage="errorMessage"
    @closeErrorModal="showErrorModal = false"
  />

  <StoreConfirmationModal
    :showConfirmModal="showConfirmModal"
    :productName="selectedProductName"
    :requiredPoints="selectedProductPoints"
    :loading="loading"
    @confirmPurchase="confirmPurchase"
    @closeConfirmModal="showConfirmModal = false"
  />

  <StoreTransactionModal
    :show="showTransactionModal"
    :transactions="transactions"
    @close="showTransactionModal = false"
  />
</template>

<script>
// import axios from 'axios';
import StoreConfirmationModal from "@/components/StoreConfirmationModal.vue";
import StoreTransactionModal from "@/components/StoreTransactionModal.vue";
import StoreSuccessModal from "@/components/StoreSuccessModal.vue";
import StoreErrorModal from "@/components/StoreErrorModal.vue";
import axios from "axios";
// import { watch } from 'vue';
// import PrevLeaderboardSection from './PrevLeaderboardSection.vue';

export default {
  data() {
    return {
      loading: false,
      points: 0,
      searchQuery: "",
      showDesktopFilter: false,
      showMobileFilter: false,
      animatedPoints: 0,
      products: [],
      showSortDropdown: false,
      selectedSortOption: "From Low to High",
      sortOptions: [
        { label: "From Low to High", value: "lowToHigh" },
        { label: "From High to Low", value: "highToLow" },
        { label: "From A to Z", value: "aToZ" },
        { label: "From Z to A", value: "zToA" },
      ],
      // categories: ["All", "Tips", "Bonus"],
      // selectedCategory: "All",
      selectedPointsRange: "",
      minPoints: "",
      maxPoints: "",
      currentPage: 1,
      itemsPerPage: 8,
      showErrorModal: false,
      errorTitle: "",
      errorMessage: "",
      showSuccessModal: false,
      showConfirmModal: false,
      showRedirectModal: false,
      hasDiscord: false,
      selectedProductId: null,
      selectedProductName: "",
      selectedProductPoints: 0,
      showTransactionModal: false,
      animatedTotalItems: 0,
      transactions: [],
    };
  },
  watch: {
    selectedSortOption(newVal) {
      this.selectedSortLabel = newVal;
    },
    totalItems(newValue) {
      this.animateNumber("animatedTotalItems", newValue);
    },
  },
  components: {
    StoreSuccessModal,
    StoreErrorModal,
    StoreTransactionModal,
    StoreConfirmationModal,
  },
  computed: {
    filteredProducts() {
      let filtered = this.products.filter((product) => product.status === "active");

      if (this.searchQuery) {
        filtered = filtered.filter((product) =>
          product.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.selectedCategory && this.selectedCategory.toLowerCase() !== "all") {
        filtered = filtered.filter(
          (product) =>
            product.category.toLowerCase() === this.selectedCategory.toLowerCase()
        );
      }

      if (this.minPoints !== "" || this.maxPoints !== "") {
        const min = this.minPoints !== "" ? Number(this.minPoints) : 0;
        const max = this.maxPoints !== "" ? Number(this.maxPoints) : Infinity;

        filtered = filtered.filter(
          (product) => product.requiredPoints >= min && product.requiredPoints <= max
        );
      }

      return filtered;
    },

    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },

    selectedSortLabel() {
      return this.selectedSortOption;
    },

    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredProducts.slice(start, start + this.itemsPerPage);
    },
    totalItems() {
      return this.filteredProducts.length;
    },
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    itemStart() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    itemEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
    visiblePages() {
      const range = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },
  },
  methods: {
    async init() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.loading = true;

      try {
        // Fetch user data
        const token = localStorage.getItem("ballyboy-access-token");

        if (token) {
          const userResponse = await axios.get(`${baseUrl}/api/user`, {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          });

          if (userResponse.data) {
            this.hasDiscord = userResponse.data.user.discordId;

            this.showRedirectModal = !this.hasDiscord;
          }

          this.animateValues();
        }
      } catch (error) {
        console.error("Error loading data", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchProducts() {
      this.loading = true;
      const baseUrl = process.env.VUE_APP_BASE_API;

      try {
        const response = await axios.get(`${baseUrl}/api/store/products`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        this.products = response.data.products || [];
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        this.loading = false;
      }
    },
    async purchaseProduct(productId) {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("ballyboy-access-token");

      if (!token) {
        this.showError("You must be logged in to make a purchase.");
        return;
      }

      try {
        const response = await axios.post(
          `${baseUrl}/api/store/purchase`,
          { productId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          this.showSuccess(response.data.ticket);
          
          this.fetchUserPoints();
          this.fetchProducts();
          this.init();
        }
      } catch (error) {
        if (error.response) {
          const { status, data } = error.response;
          if (status === 402) {
            this.showError("⚠️ Insufficient points to purchase this product.");
          } else if (status === 422) {
            this.showError("⚠️ Invalid input: Product ID is required.");
          } else {
            this.showError(`❌ Error: ${data.message}`);
          }
        } else {
          this.showError("❌ Failed to process purchase. Please try again.");
        }
      }
    },
    async fetchUserPoints() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("ballyboy-access-token");

      if (!token) {
        console.error("User not authenticated.");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/user/points`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
            Authorization: `Bearer ${token}`,
          },
        });

        this.points = response.data.points;

        this.animateValues();
      } catch (error) {
        console.error("Error fetching user points:", error);
      }
    },
    async fetchTransactions() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("ballyboy-access-token");

      if (!token) {
        console.error("User not authenticated.");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/user/purchase/transactions`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        if (response.status === 200) {
          this.transactions = response.data.purchaseTransactions
            ? response.data.purchaseTransactions.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
              )
            : [];
          this.showTransactionModal = true;
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    },
    async confirmPurchase() {
      if (!this.selectedProductId) {
        this.showError("❌ No product selected.");
        return;
      }

      this.loading = true;

      try {
        await this.purchaseProduct(this.selectedProductId);
        this.showConfirmModal = false;
      } catch (error) {
        console.error("Purchase failed:", error);
      } finally {
        this.loading = false;
      }
    },
    showSuccess(ticket) {
      this.ticket = ticket;
      this.showSuccessModal = true;
    },
    showError(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
    },

    showConfirm(product) {
      this.selectedProductId = product._id;
      this.selectedProductName = product.title;
      this.selectedProductPoints = product.requiredPoints;

      if (this.points < product.requiredPoints) {
        this.errorTitle = "Insufficient Points";
        this.errorMessage = "You don't have enough points to complete this purchase.";
        this.showErrorModal = true;
        return;
      }

      this.showConfirmModal = true;
    },
    animateValues() {
      this.animateNumber("animatedPoints", this.points);
    },
    animateNumber(variable, targetValue) {
      let startValue = this[variable] || 0;
      if (startValue === targetValue) return;

      const step = Math.max(1, Math.abs(targetValue - startValue) / 20); // Ensures step is at least 1
      const isNegative = startValue > targetValue;

      const interval = setInterval(() => {
        if (isNegative) {
          startValue -= step;
          if (startValue <= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        } else {
          startValue += step;
          if (startValue >= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        }

        // Ensure whole numbers with commas
        this[variable] = Math.round(startValue).toLocaleString();
      }, 50);
    },
    toggleDesktopFilter() {
      this.showDesktopFilter = !this.showDesktopFilter;
      console.log("desktop dropdown working");
    },
    toggleMobileFilter() {
      this.showMobileFilter = !this.showMobileFilter;
      console.log("mobile dropdown working");
    },
    applyFilter() {
      this.currentPage = 1;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    applySort(option) {
      this.selectedSortOption = option.label;
      this.sortProducts(option.value);
      this.showSortDropdown = false; // Close dropdown after selection
    },
    sortProducts(order) {
      if (order === "highToLow") {
        this.products.sort((a, b) => b.requiredPoints - a.requiredPoints);
      } else if (order === "lowToHigh") {
        this.products.sort((a, b) => a.requiredPoints - b.requiredPoints);
      } else if (order === "aToZ") {
        this.products.sort((a, b) => a.title.localeCompare(b.title));
      } else if (order === "zToA") {
        this.products.sort((a, b) => b.title.localeCompare(a.title));
      }
    },
  },
  mounted() {
    this.init();
    this.fetchProducts();
    this.fetchUserPoints();
  },
};
</script>
